import PropTypes from 'prop-types';
import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, Box, Typography, Switch } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import ReadOnlyTextField from 'src/components/widgets/readonly-textfield';
import QuillEditor from 'src/components/quill-editor';
import SubmitButton, { useCustomFormik } from 'src/components/formik-helper';
import AvatarDisplay from 'src/components/widgets/avatar-display';
import StringTextField from 'src/components/widgets/string-textfield';
import { mergeNoteFromEditor } from 'src/utils/formatters';
import { useTranslation } from 'react-i18next';
import { appStr } from 'src/locales/app_strings'
import {
  initialValues,
  validationSchema,
  handleSubmitted,
} from './formik';

export const DetailView = forwardRef(({ item, apiUpdate, apiDelete, blockAccount, unlockAccount, ...other }, ref) => {
  const editorRef = useRef();
  const [element, setElement] = useState({});
  const { t } = useTranslation();

  const formik = useCustomFormik({
    initialValues: {
      isRestored: false,
    },
    validationSchema: validationSchema(),
    onSubmit: async (values, helpers) => {
      const mergeValues = mergeNoteFromEditor(values, editorRef, item.note);
      await handleSubmitted(mergeValues, item, apiUpdate, apiDelete, blockAccount, unlockAccount);
    },
  });

  useEffect(() => {
    initialValues(formik, item);
    
    if (item) {  
      setElement(prev => ({
        ...prev,
        status: item.status,
        statusName: item.statusName,
        statusVerify: item.statusVerify,
        isUnlock: false,
        isVerified: false,
      }));
    } 
  }, [item]);

  useImperativeHandle(ref, () => ({
    setDataFromParent: (value) => {
      setElement(prev => ({
        ...prev,
        ...value,
      }));
      if(formik){
        formik.setFieldValue('status', value.status);
        formik.setFieldValue('isRestored', false);
        formik.setFieldValue('isVerified', false);
      }
    },
  }));

  return (
    <Card>
      <CardContent sx={{ px: 1, py: 2 }}>
      <form onSubmit={formik.handleSubmit} {...other}>
        <Grid container spacing={2}>
          <Grid xs={12} md={9}>
            <div>
            <Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <StringTextField label="Họ tên" name="nameField" formik={formik} hintValue={item.name} />
              </Grid>

              <Grid xs={12} md={3}>
                <StringTextField label="Sinh nhật" name="birthdayField" formik={formik} hintValue={item.birthday} />
              </Grid>

              <Grid xs={12} md={3}>
                <ReadOnlyTextField label="Code" value={item.code} enumType='code' />
              </Grid>

              <Grid xs={12} md={3}>
                <StringTextField label="Số điện thoại" name="phoneField" formik={formik} hintValue={item.phone} />
               
              </Grid>

              <Grid xs={12} md={3}>
                <StringTextField label="Căn cước" name="cardField" formik={formik} hintValue={item.identityCard} />
              </Grid>

              <Grid xs={12} md={6}>
                <StringTextField label="Email" name="email" formik={formik} hintValue={item.email} />
              </Grid>

              <Grid xs={12} md={12}>
                <StringTextField label="Địa chỉ" name="address" formik={formik} hintValue={item.address} />
              </Grid>
            </Grid>
            </div>
          </Grid>
          <Grid xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <AvatarDisplay avatar={item.avatar} gender={item.gender} />
          </Grid>
        </Grid>

        <Grid xs={12} md={12} sx={{ mt: 3}}>
          <div>
          <Grid container spacing={3}>
            <Grid xs={12} md={3}>
              <ReadOnlyTextField label="Phân loại" value={item.partnerType} />
            </Grid>
            <Grid xs={12} md={3}>
              <ReadOnlyTextField label="Thuộc nhóm" value={item.groupName} />
            </Grid>
            <Grid xs={12} md={3}>
              <ReadOnlyTextField label={t(appStr.txt_071)} value={item.firstCode} enumType='code' />
            </Grid>
            <Grid xs={12} md={3}>
              <ReadOnlyTextField label={t(appStr.txt_072)} value={item.secondCode} enumType='code' />
            </Grid>
            <Grid xs={12} md={3}>
              <ReadOnlyTextField label={t(appStr.txt_073)} value={item.degree} />
            </Grid>
            <Grid xs={12} md={3}>
              <ReadOnlyTextField label="Khóa" value={item.course} />
            </Grid>
            <Grid xs={12} md={6}>
              <ReadOnlyTextField label="Đào tạo" value={item.options} />
            </Grid>
          </Grid>
          </div>
        </Grid>
        
        <Grid xs={12} md={12} sx={{ mt: 2}}>
          <div>
            <Grid container spacing={3} sx={{ alignItems: 'center' }}>
              <Grid xs={12}>
                <QuillEditor
                  ref={editorRef}
                  value={item.note}
                  sx={{ height: 130 }}
                />
              </Grid>

              <Grid xs={12} md={4} sx={{ position: 'relative' }}>
                {element.status === 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: 4,
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'background.paper',
                      px: 1,
                      borderRadius: 1,
                      zIndex: 1,
                    }}
                  >
                    <Typography color="text.primary" sx={{ mr: 1 }}>
                      Mở khóa
                    </Typography>
                    <Switch
                      color="primary"
                      edge="start"
                      onChange={(event) => {
                        const isChecked = event.target.checked;
                        setElement(prev => ({
                          ...prev,
                          isUnlock: isChecked,
                        }));
                        if(formik){
                          if(isChecked){
                            setElement(prev => ({
                              ...prev,
                              isVerified: false,
                            }));
                          }
                          formik.setFieldValue('isRestored', isChecked);
                        }
                      }}
                    />
                  </Box>
                )}
                <ReadOnlyTextField label="Trạng thái" value={element.statusName} />
              </Grid>

              <Grid xs={12} md={7} display="flex" 
                sx={{ justifyContent: { xs: 'center', md: 'flex-end' } }}
              >
                <SubmitButton formik={formik} 
                  isRemove={!element.isUnlock} 
                  labelRemove={element.statusVerify}
                  hasVerified={element.isVerified} 
                  onVerifyChange={(isChecked) => {
                    setElement(prev => ({
                      ...prev,
                      isVerified: isChecked,
                    }));
                  }}
                  />
              </Grid>
            </Grid>
          </div>
        </Grid>
        </form>
      </CardContent>
    </Card>
  );
});

DetailView.propTypes = {
  item: PropTypes.object.isRequired,
  apiUpdate: PropTypes.func,
  apiDelete: PropTypes.func,
};